import React, { useMemo } from "react"
import {
    Box, Card, CardContent, CardHeader, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    ToggleButton, ToggleButtonGroup, Typography
} from "@mui/material"
import BarChartIcon from '@mui/icons-material/BarChart'
import TableChartIcon from '@mui/icons-material/TableChart'
import _ from "lodash"
import { formatNumber, numberPrecision } from "../../../../helpers/UtilityHelper"
import { HorizontalStackedBarChart } from "../../../charts/BarChart"
import useGetCourier from "../useGetCourier"
import { TableSkeleton } from "../../../../common/Skeletons"
import { STATUS_COLORS } from "../../../charts/colors"

const DELIVERY_COLOR = STATUS_COLORS.DELIVERED
const INTRANSIT_COLOR = STATUS_COLORS.INTRANSIT
const RTO_COLOR = STATUS_COLORS.RTO
const OTHER_COLOR = STATUS_COLORS.CANCELLED

const STATUS_SERIES = [
    { dataKey: 'deliveredPct', label: 'Delivered %', fill: DELIVERY_COLOR, unit: '%' },
    { dataKey: 'inTransitPct', label: 'In-Transit %', fill: INTRANSIT_COLOR, unit: '%' },
    { dataKey: 'rtoPct', label: 'RTO %', fill: RTO_COLOR, unit: '%' },
    { dataKey: 'otherPct', label: 'Other %', fill: OTHER_COLOR, unit: '%' },
]

const BASE_CHART_OPTIONS = {
    yaxis: 'name',
    xLabel: '% Shipments',
    stackId: 'stack',
    domain: [0, 101],
    data: STATUS_SERIES,
}

const headerCellSx = {
    backgroundColor: '#FFEB3B',
    fontWeight: 'bold',
    fontSize: '0.75rem',
    border: '1px solid #ccc',
    whiteSpace: 'nowrap',
    padding: '6px 10px',
}

const dataCellSx = {
    border: '1px solid #ccc',
    fontSize: '0.75rem',
    padding: '5px 10px',
}

function getPctBg(key, val) {
    if (key === 'deliveredPct') {
        if (val >= 70) return '#c8e6c9'
        if (val >= 50) return '#fff9c4'
        return '#ffccbc'
    }
    if (key === 'rtoPct') {
        if (val >= 30) return '#ffcdd2'
        if (val >= 15) return '#fff9c4'
        return '#c8e6c9'
    }
    if (key === 'inTransitPct') {
        if (val >= 30) return '#fff9c4'
        return 'inherit'
    }
    return 'inherit'
}

function SummaryKpi({ label, value, color }) {
    return (
        <Paper elevation={0} sx={{
            p: 1.5,
            textAlign: 'center',
            borderTop: `4px solid ${color}`,
            borderRadius: 2,
            backgroundColor: '#fff',
            border: `1px solid #e0e0e0`,
            borderTopColor: color,
        }}>
            <Typography sx={{ fontWeight: 'bold', color, fontSize: '1.1rem' }}>{value}%</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>{label}</Typography>
        </Paper>
    )
}

export default function CourierWiseDeliveryComparison({ data, courierIds }) {
    const { data: allCouriers, isLoading, isFetching } = useGetCourier()
    const [view, setView] = React.useState('chart')

    const allPresentData = useMemo(() => {
        if (!allCouriers || !courierIds) return []
        return allCouriers
            .filter(row => courierIds.includes(row.id))
            .map(row => ({ id: row.id, name: row.parent || row.name || row.id }))
    }, [allCouriers, courierIds])

    const aggregatedData = useMemo(() => {
        if (!data?.lineChartData || allPresentData.length === 0) return []

        return allPresentData.map(item => {
            const totalShipments = _.sumBy(data.lineChartData, `courier_${item.id}_value`) || 0
            if (totalShipments === 0) return null

            const deliveredCount = _.sumBy(data.lineChartData, `courier_${item.id}_deliveredCount`) || 0
            const inTransitCount = _.sumBy(data.lineChartData, `courier_${item.id}_inTransitCount`) || 0
            const rtoCount = _.sumBy(data.lineChartData, `courier_${item.id}_rtoCount`) || 0
            const otherCount = Math.max(0, totalShipments - deliveredCount - inTransitCount - rtoCount)

            return {
                name: item.name?.replace(/_/g, ' '),
                courierId: item.id,
                totalShipments,
                deliveredCount,
                inTransitCount,
                rtoCount,
                otherCount,
                deliveredPct: Number(numberPrecision((deliveredCount / totalShipments) * 100)),
                inTransitPct: Number(numberPrecision((inTransitCount / totalShipments) * 100)),
                rtoPct: Number(numberPrecision((rtoCount / totalShipments) * 100)),
                otherPct: Number(numberPrecision((otherCount / totalShipments) * 100)),
            }
        })
            .filter(Boolean)
            .sort((a, b) => b.deliveredPct - a.deliveredPct)
    }, [data, allPresentData])

    const grandTotal = useMemo(() => {
        if (aggregatedData.length === 0) return null
        const totalShipments = _.sumBy(aggregatedData, 'totalShipments')
        const deliveredCount = _.sumBy(aggregatedData, 'deliveredCount')
        const inTransitCount = _.sumBy(aggregatedData, 'inTransitCount')
        const rtoCount = _.sumBy(aggregatedData, 'rtoCount')
        return {
            totalShipments,
            deliveredCount,
            inTransitCount,
            rtoCount,
            deliveredPct: numberPrecision((deliveredCount / totalShipments) * 100),
            inTransitPct: numberPrecision((inTransitCount / totalShipments) * 100),
            rtoPct: numberPrecision((rtoCount / totalShipments) * 100),
        }
    }, [aggregatedData])

    if (isLoading || isFetching) return <TableSkeleton rows={5} />
    if (aggregatedData.length === 0) return null

    const maxNameLength = Math.max(...aggregatedData.map(r => (r.name || '').length), 10)
    const resolvedChartOptions = {
        ...BASE_CHART_OPTIONS,
        yLabel: 'Courier',
        yWidth: Math.min(Math.max(maxNameLength * 7, 120), 220),
    }

    return (
        <Card elevation={1}>
            <CardHeader
                title="Courier Wise Delivery Performance Comparison"
                subheader="Compares delivery performance across courier partners. Sorted by delivery percentage (highest first)."
                action={
                    <ToggleButtonGroup
                        color="primary"
                        size="small"
                        exclusive
                        value={view}
                        onChange={(_e, v) => v && setView(v)}
                    >
                        <ToggleButton value="chart" title="Chart">
                            <BarChartIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="table" title="Table">
                            <TableChartIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                }
            />

            {grandTotal && (
                <Box sx={{ px: 2, pb: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                    <SummaryKpi label="Overall Delivery %" value={grandTotal.deliveredPct} color={DELIVERY_COLOR} />
                    <SummaryKpi label="Overall In-Transit %" value={grandTotal.inTransitPct} color={INTRANSIT_COLOR} />
                    <SummaryKpi label="Overall RTO %" value={grandTotal.rtoPct} color={RTO_COLOR} />
                </Box>
            )}

            <CardContent sx={{ pt: 1 }}>
                {view === 'chart' && (
                    <HorizontalStackedBarChart
                        data={aggregatedData}
                        chartOptions={resolvedChartOptions}
                        height={Math.max(400, aggregatedData.length * 36)}
                    />
                )}

                {view === 'table' && (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 600 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...headerCellSx, position: 'sticky', left: 0, zIndex: 2 }}>Courier</TableCell>
                                    <TableCell align="center" sx={headerCellSx}>Total Shipments</TableCell>
                                    <TableCell align="center" sx={{ ...headerCellSx, backgroundColor: '#c8e6c9' }}>Delivered</TableCell>
                                    <TableCell align="center" sx={{ ...headerCellSx, backgroundColor: '#c8e6c9' }}>Delivery %</TableCell>
                                    <TableCell align="center" sx={{ ...headerCellSx, backgroundColor: '#bbdefb' }}>In-Transit</TableCell>
                                    <TableCell align="center" sx={{ ...headerCellSx, backgroundColor: '#bbdefb' }}>In-Transit %</TableCell>
                                    <TableCell align="center" sx={{ ...headerCellSx, backgroundColor: '#ffcdd2' }}>RTO</TableCell>
                                    <TableCell align="center" sx={{ ...headerCellSx, backgroundColor: '#ffcdd2' }}>RTO %</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {aggregatedData.map((row) => (
                                    <TableRow key={row.courierId} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                        <TableCell sx={{ fontWeight: 500, ...dataCellSx, position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                            {row.name}
                                        </TableCell>
                                        <TableCell align="center" sx={dataCellSx}>
                                            {formatNumber(row.totalShipments)}
                                        </TableCell>
                                        <TableCell align="center" sx={dataCellSx}>
                                            {formatNumber(row.deliveredCount)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ ...dataCellSx, backgroundColor: getPctBg('deliveredPct', row.deliveredPct), fontWeight: 'bold' }}>
                                            {numberPrecision(row.deliveredPct)}%
                                        </TableCell>
                                        <TableCell align="center" sx={dataCellSx}>
                                            {formatNumber(row.inTransitCount)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ ...dataCellSx, backgroundColor: getPctBg('inTransitPct', row.inTransitPct) }}>
                                            {numberPrecision(row.inTransitPct)}%
                                        </TableCell>
                                        <TableCell align="center" sx={dataCellSx}>
                                            {formatNumber(row.rtoCount)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ ...dataCellSx, backgroundColor: getPctBg('rtoPct', row.rtoPct), fontWeight: 'bold' }}>
                                            {numberPrecision(row.rtoPct)}%
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {grandTotal && (
                                    <TableRow sx={{ backgroundColor: '#212121' }}>
                                        <TableCell sx={{ color: '#fff', fontWeight: 'bold', ...dataCellSx, borderColor: '#555', position: 'sticky', left: 0, backgroundColor: '#212121', zIndex: 1 }}>
                                            Grand Total
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', ...dataCellSx, borderColor: '#555' }}>
                                            {formatNumber(grandTotal.totalShipments)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', ...dataCellSx, borderColor: '#555' }}>
                                            {formatNumber(grandTotal.deliveredCount)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', ...dataCellSx, borderColor: '#555' }}>
                                            {grandTotal.deliveredPct}%
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', ...dataCellSx, borderColor: '#555' }}>
                                            {formatNumber(grandTotal.inTransitCount)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', ...dataCellSx, borderColor: '#555' }}>
                                            {grandTotal.inTransitPct}%
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', ...dataCellSx, borderColor: '#555' }}>
                                            {formatNumber(grandTotal.rtoCount)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', ...dataCellSx, borderColor: '#555' }}>
                                            {grandTotal.rtoPct}%
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    )
}
